import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { MENU_LIST_STYLE, menuCheck } from '../../constants';

export type IconTone = 'purple' | 'error' | 'warning' | 'subtle' | 'violet';

const TONE: Record<IconTone, string> = {
  purple: 'var(--purple-100)',
  error: 'var(--surface-error-card)',
  warning: 'var(--surface-warning-card)',
  subtle: 'var(--surface-subtle)',
  violet: 'var(--violet-200)'
};

// Vectors export at stroke bounds, so the box is fixed and the image sits inside.
export function Glyph({ src, box }: { src: string; box: number }) {
  return (
    <span className='flex shrink-0 items-center justify-center' style={{ width: box, height: box }}>
      <img alt='' src={src} className='protocol-glyph block max-w-none' />
    </span>
  );
}

/** `width` fixes the list; `minWidth` is a floor it can still grow past for a long row. */
export function MenuList({ children, width, minWidth }: { children: ReactNode; width?: number; minWidth?: number }) {
  const style = width ? { width } : { ...MENU_LIST_STYLE, ...(minWidth ? { minWidth } : {}) };
  return (
    <div className='flex shrink-0 flex-col items-stretch gap-2.5' style={style}>
      {children}
    </div>
  );
}

/** Space between rows; 2px by default keeps the highlights apart. */
export function MenuGroup({
  label,
  children,
  width,
  rowGap = 2
}: {
  label?: string;
  children: ReactNode;
  width?: number;
  rowGap?: number;
}) {
  return (
    <div className='flex shrink-0 flex-col items-start gap-1.5' style={{ width: width ?? '100%' }}>
      {label ? (
        <div className='flex w-full shrink-0 items-center px-2.5'>
          <p className='whitespace-nowrap text-[10px] font-medium leading-[1.1] text-[var(--grey-80)]'>{label}</p>
        </div>
      ) : null}
      <div className='flex w-full shrink-0 flex-col items-stretch' style={{ gap: rowGap }}>
        {children}
      </div>
    </div>
  );
}

export function MenuDivider() {
  return (
    <div className='h-px w-full shrink-0 px-2.5'>
      <div className='h-px w-full bg-[var(--grey-40)]' />
    </div>
  );
}

export interface MenuItemBodyProps {
  label: string;
  // A glyph src, or any element (e.g. a lucide icon) drawn bare.
  icon?: string | ReactNode;
  tone?: IconTone;
  // "filled" is the tinted 18px chip the tool menu uses; "bare" the plain 16px icon.
  iconStyle?: 'filled' | 'bare';
  checked?: boolean;
  destructive?: boolean;
}

// Menu-Building Blocks row content: icon chip, 13px label, trailing check.
export function MenuItemBody({ label, icon, tone = 'subtle', iconStyle = 'filled', checked = false, destructive = false }: MenuItemBodyProps) {
  return (
    <span className='flex min-w-px flex-1 items-center justify-between gap-2'>
      <span className='flex min-w-px flex-1 items-center gap-2'>
        {typeof icon === 'string' ? (
          iconStyle === 'bare' ? (
            <Glyph src={icon} box={16} />
          ) : (
            <span className='flex size-4.5 shrink-0 items-center justify-center rounded-[4px] p-0.5' style={{ background: TONE[tone] }}>
              <Glyph src={icon} box={14.25} />
            </span>
          )
        ) : (
          icon
        )}
        <span
          className={cn(
            'min-w-px flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-[13px] leading-4.5',
            destructive ? 'text-[var(--text-error)]' : 'text-[var(--text-body)]'
          )}
        >
          {label}
        </span>
      </span>
      {checked ? <Glyph src={menuCheck} box={16} /> : null}
    </span>
  );
}

export function MenuItem({
  selected = false,
  disabled = false,
  onSelect,
  children,
  ...body
}: MenuItemBodyProps & {
  selected?: boolean;
  disabled?: boolean;
  onSelect?: () => void;
  children?: ReactNode;
}) {
  return (
    <button
      type='button'
      role='menuitem'
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        'flex w-full min-w-12 shrink-0 cursor-pointer items-center rounded-lg p-px text-left transition-colors',
        selected ? 'bg-[var(--surface-subtle)]' : 'hover:bg-[var(--surface-subtle)] active:bg-[var(--neutral-200)]',
        disabled && 'cursor-not-allowed opacity-50 hover:bg-transparent active:bg-transparent'
      )}
    >
      <span className='flex min-w-px flex-1 items-center gap-2 px-2.5 py-1'>
        <MenuItemBody {...body} />
        {children}
      </span>
    </button>
  );
}
